import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Loader } from 'sonata-react-components';

import './countDown.scss';

type Props = {
  countdownShown: boolean;
  onCancel?: () => void;
  onMinimize?: () => void;
  onFinish?: () => void;
};

type State = {
  seconds: number;
};

export function CountDown({ countdownShown, onCancel, onMinimize, onFinish }: Props) {
  const { formatMessage } = useIntl();
  const timerRef = useRef<number | null>(null);
  const finishedRef = useRef(false);

  const [{ seconds }, setState] = useState<State>(() => {
    if (countdownShown) {
      timerRef.current = window.setInterval(() => {
        setState(s => ({ seconds: s.seconds - 1 }));
      }, 1000);
    }
    return { seconds: 8 };
  });

  // The timer outlives the component when it is unmounted before reaching 0 —
  // cancelling the process, or the caller closing on completion.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  if (seconds <= 0 && !finishedRef.current) {
    finishedRef.current = true;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onFinish?.();
  }

  const handleMinimize = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    onMinimize?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  return (
    <>
      <div className="countdown">
        <Loader backgroundColor="transparent" id={'countdown-spinner'} type="landing" variant="default">
          <Loader.Title className="countdown__text">
            {formatMessage({
              defaultMessage:
                'Loading... This screen will minimize to let you keep working while you wait for the file to be processed.',
              id: 'attributionPOI.modal.countdown.processing'
            })}
          </Loader.Title>
        </Loader>
        {countdownShown && (
          <div className="countdown__timerContainer">
            <p className="countdown__timer">{seconds}</p>
            <p className="countdown__timerText">
              {formatMessage({
                defaultMessage: 's',
                id: 'attributionPOI.modal.countdown.secondsRemaining'
              })}
            </p>
          </div>
        )}
        <ButtonUI
          icon="fa-regular fa-arrows-minimize"
          iconPosition="right"
          onClick={handleMinimize}
          shape="link"
          size="small"
          text={formatMessage({
            defaultMessage: 'Minimize screen',
            id: 'attributionPOI.modal.countdown.minimizeScreen'
          })}
          type="secondary"
        />
      </div>
      <div className="countdownCancelContainer">
        <ButtonUI
          onClick={handleCancel}
          shape="squareRounded"
          size="medium"
          text={formatMessage({
            defaultMessage: 'Cancel',
            id: 'attributionPOI.modal.countdown.cancel'
          })}
          type="secondary"
        />
      </div>
    </>
  );
}
